import { SyntheticEvent } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { TabContext } from '@mui/lab';
import {
  Drawer,
  Flex,
  FlexCenter,
  FlexColumn,
  Tab,
  Tabs,
} from '@elementary/common-ui/components';
import { INCIDENT_OVERVIEW_TAB } from '@elementary/common-ui/constants/urlParams';
import PanelError from 'shared/components/PanelError';
import { ErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import { SourceType } from 'shared/types';
import useIncidentOverviewSidePanel from '../hooks/useIncidentOverviewSidePanel';
import useIncidentDetails from '../hooks/useIncidentDetails';
import IncidentOverviewDrawerTitle from './IncidentOverviewDrawerTitle';
import IncidentOverviewHeader from './IncidentOverviewHeader';
import IncidentOverviewTabs from './IncidentOverviewTabs';
import IncidentMetadata from './IncidentMetadata';
import IncidentSummary from './IncidentSummary';

type TabValue = 'timeline' | 'execution' | 'dependencies';

type TabConfig = { label: string; value: TabValue };
type SourceTabConfig = { tabs: TabConfig[]; defaultTab: TabValue };

const TAB_CONFIGS: Record<SourceType, SourceTabConfig> = {
  [SourceType.TEST]: {
    tabs: [
      { label: 'Test Execution Info', value: 'execution' },
      { label: 'Test Dependencies', value: 'dependencies' },
      { label: 'Timeline', value: 'timeline' },
    ],
    defaultTab: 'execution',
  },
  [SourceType.MODEL]: {
    tabs: [
      { label: 'Asset Execution Info', value: 'execution' },
      { label: 'Asset Dependencies', value: 'dependencies' },
      { label: 'Timeline', value: 'timeline' },
    ],
    defaultTab: 'execution',
  },
  [SourceType.PIPELINE]: {
    tabs: [
      { label: 'Info', value: 'execution' },
      { label: 'Timeline', value: 'timeline' },
    ],
    defaultTab: 'execution',
  },
};

const TabsWrapper = styled(Flex)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  flex-shrink: 0;
`;

const ErrorLoadingWrapper = styled(FlexCenter)`
  height: 100%;
  padding: 0 24px;
`;

const Content = styled(FlexColumn)`
  height: 100%;
  overflow: hidden;
  padding: 24px;
  gap: 16px;
`;

const IncidentOverviewSidePanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab =
    (searchParams.get(INCIDENT_OVERVIEW_TAB) as TabValue) || 'execution';

  const handleTabChange = (_event: SyntheticEvent, value: TabValue) => {
    setSearchParams((prev) => {
      prev.set(INCIDENT_OVERVIEW_TAB, value);
      return prev;
    });
  };

  const { selectedIncidentId, isOpen, onClose } =
    useIncidentOverviewSidePanel();
  const {
    data: incident,
    isLoading,
    error,
  } = useIncidentDetails(selectedIncidentId);

  useViewLoadTracking({
    name: 'Incident overview panel',
    loadDeps: [incident],
    contextId: selectedIncidentId || undefined,
  });

  const tabConfig = incident
    ? TAB_CONFIGS[incident.sourceType] ?? TAB_CONFIGS[SourceType.TEST]
    : TAB_CONFIGS[SourceType.TEST];
  const validTabValues = tabConfig.tabs.map((t) => t.value);
  const effectiveTab = validTabValues.includes(currentTab)
    ? currentTab
    : tabConfig.defaultTab;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={<IncidentOverviewDrawerTitle />}
      width="medium"
      noPadding
    >
      <ErrorBoundary
        resetKeys={[selectedIncidentId]}
        fallbackRender={({ error }) => (
          <ErrorLoadingWrapper>
            <PanelError
              error={error}
              page="IncidentOverviewSidePanel"
              errorName="ReactRenderingError"
            />
          </ErrorLoadingWrapper>
        )}
      >
        {!!error && (
          <ErrorLoadingWrapper>
            <PanelError
              error={error as Error}
              page="IncidentOverviewSidePanel"
              errorName="ApiError"
            />
          </ErrorLoadingWrapper>
        )}

        {isLoading ? (
          <ErrorLoadingWrapper>
            <CircularProgress />
          </ErrorLoadingWrapper>
        ) : (
          incident &&
          !error &&
          !isLoading && (
            <Content>
              <Box sx={{ flexShrink: 0 }}>
                <IncidentOverviewHeader incident={incident} />

                <Box sx={{ pt: 1, pb: 2 }}>
                  <IncidentMetadata incident={incident} />
                </Box>

                <IncidentSummary
                  incidentId={incident.id}
                  summaries={incident.summaries}
                />
              </Box>

              <TabContext value={effectiveTab}>
                <FlexColumn
                  sx={{
                    minHeight: '60%',
                  }}
                >
                  <TabsWrapper>
                    <Tabs value={effectiveTab} onChange={handleTabChange}>
                      {tabConfig.tabs.map((tab) => (
                        <Tab
                          key={tab.value}
                          label={tab.label}
                          value={tab.value}
                          wrapped
                        />
                      ))}
                    </Tabs>
                  </TabsWrapper>

                  <IncidentOverviewTabs
                    incident={incident}
                    currentTab={effectiveTab}
                  />
                </FlexColumn>
              </TabContext>
            </Content>
          )
        )}
      </ErrorBoundary>
    </Drawer>
  );
};

export default IncidentOverviewSidePanel;
